import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import { updatePageSEO, createBreadcrumbSchema, createPageSchema, cleanupDynamicSchemas } from '../../utils/seo';

export default function PrivacyPage() {
  useEffect(() => {
    // SEO設定
    updatePageSEO({
      title: 'プライバシーポリシー｜永山の勉強アーカイブ - 個人情報の取り扱い',
      description: '永山の勉強アーカイブのプライバシーポリシー。個人情報の取り扱い、Cookie、アクセス解析ツールの使用について説明しています。ユーザーのプライバシーを尊重し、個人情報の保護に努めます。',
      keywords: 'プライバシーポリシー,個人情報保護,永山の勉強アーカイブ,Cookie',
      canonical: 'https://nagayama-study-archive.readdy.ai/privacy',
      ogType: 'website',
    });

    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: 'プライバシーポリシー', url: 'https://nagayama-study-archive.readdy.ai/privacy' },
    ]);

    createPageSchema({
      name: 'プライバシーポリシー',
      description: '個人情報の取り扱い、Cookie、アクセス解析ツールの使用について説明しています。',
      url: 'https://nagayama-study-archive.readdy.ai/privacy',
      type: 'WebPage',
    });

    return () => {
      cleanupDynamicSchemas();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-16 sm:py-20 bg-gradient-to-r from-teal-600 to-blue-600 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <FadeInSection>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                <strong>プライバシーポリシー</strong>
              </h1>
              <p className="text-base sm:text-lg text-teal-50 max-w-2xl mx-auto">
                個人情報の取り扱いについて
              </p>
            </FadeInSection>
          </div>
        </section>

        {/* Content */}
        <FadeInSection>
          <section className="py-16">
            <div className="max-w-3xl mx-auto px-6">
              <div className="prose prose-gray max-w-none">
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">1. はじめに</h2>
                  <p className="text-gray-600 leading-relaxed">
                    永山の勉強アーカイブ（以下「当サイト」）は、ユーザーのプライバシーを尊重し、個人情報の保護に努めます。本プライバシーポリシーは、当サイトにおける個人情報の取り扱いについて説明するものです。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">2. 情報の利用目的</h2>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    収集した情報は、以下の目的で利用します。
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li>お問い合わせへの対応</li>
                    <li>サイトの改善・運営</li>
                    <li>利用状況の分析</li>
                  </ul>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">3. 情報の第三者提供</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトは、法令に基づく場合を除き、ユーザーの同意なく個人情報を第三者に提供することはありません。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">4. Cookieの使用</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトでは、ユーザー体験の向上やアクセス解析のためにCookieを使用する場合があります。ブラウザの設定によりCookieを無効にすることも可能ですが、一部の機能が制限される場合があります。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">5. アクセス解析ツール</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトでは、Googleアナリティクス等のアクセス解析ツールを使用する場合があります。これらのツールはCookieを使用してデータを収集しますが、個人を特定する情報は含まれません。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">6. プライバシーポリシーの変更</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトは、必要に応じて本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、当サイトに掲載した時点から効力を生じるものとします。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">7. お問い合わせ</h2>
                  <p className="text-gray-600 leading-relaxed">
                    本プライバシーポリシーに関するお問い合わせは、<a href="/contact" className="text-teal-600 hover:underline">お問い合わせフォーム</a>よりご連絡ください。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>
      </main>

      <Footer />
    </div>
  );
}
